/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-empty-interface */
import { memo, useEffect, useState, useRef, useCallback } from "react";
import Camera from "@zenith/assets/icons/Camera";
import Eye from "@zenith/assets/icons/Eye";
import Home from "@zenith/assets/icons/Home";
import Info from "@zenith/assets/icons/Info";
import Smile from "@zenith/assets/icons/Smile";
import { OnboardingApiService } from "@zenith/services/user";
import {
  SelfieStatusResponseDTO,
  ImageSelfieRequestDTO,
} from "@zenith/services/user";
import { ModalComponent, SpinnerComponent } from "@zenith/components";
import { AppNavigation, AppStepper } from "@zenith/hooks";
import Clock from "@zenith/assets/icons/Clock";
import CloseCircle from "@zenith/assets/icons/CloseCircle";
import { ACCOUNT_CREATED, SIGN_UP } from "@zenith/routes/paths";
import { useMobileVerificationContext } from "@zenith/context";
import {
  SELFIE_JOB_ID_SIGNUP,
  CONNECTION_ERROR_MESSAGE,
  SUCCESS_API_CODE,
  SELFIE_VERIFICATION_COUNTDOWN_SECONDS,
  SELFIE_VERIFICATION_SLOW_THRESHOLD_SECONDS,
} from "@zenith/data/constants";
import GreenTick from "@zenith/assets/icons/components/GreenTick";
import { countryCodeToEnum } from "@zenith/utils";
import WalletIcon from "@zenith/assets/icons/WalletIcon";
import { Logo } from "@zenith/shared";

export interface SignUpPhotoVerificationPageProps {}

// Custom hook for Smile ID Web Component
const useSmileIdWebComponent = () => {
  const [isComponentLoaded, setIsComponentLoaded] = useState(false);

  useEffect(() => {
    const markReady = () => {
      if (
        customElements.get("selfie-capture-screens") ||
        customElements.get("smart-camera-web")
      ) {
        setIsComponentLoaded(true);
        return;
      }
      Promise.race([
        customElements.whenDefined("smart-camera-web"),
        customElements.whenDefined("selfie-capture-screens"),
      ]).then(() => setIsComponentLoaded(true));
    };

    const existingScript = document.getElementById("smile-id-script-v11");

    if (!existingScript) {
      const script = document.createElement("script");
      script.id = "smile-id-script-v11";
      script.type = "module";
      script.src = "https://cdn.smileidentity.com/js/v11/smart-camera-web.js";
      script.onload = markReady;
      script.onerror = () => console.error("Failed to load Smile ID script");
      document.head.appendChild(script);
    } else {
      markReady();
    }
  }, []);

  return { isComponentLoaded };
};

const SignUpPhotoVerificationPage = memo(function SignUpPhotoVerificationPage(
  props: SignUpPhotoVerificationPageProps
) {
  const { nextStep } = AppStepper();
  const { navigateTo } = AppNavigation();
  const {
    mobileVerificationContext: { phoneNumber, countryCode },
    setFirstName,
  } = useMobileVerificationContext();
  // Restore session on page refresh only (not when navigating here from another page).
  // Clears on unmount so navigating away and returning always starts fresh.
  useEffect(() => {
    const navEntry = performance.getEntriesByType("navigation")[0] as
      | PerformanceNavigationTiming
      | undefined;
    const isRefresh = navEntry?.type === "reload";

    const stored = sessionStorage.getItem(SELFIE_JOB_ID_SIGNUP);

    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (isRefresh && parsed.phoneNumber === phoneNumber && parsed.jobId) {
          setJobId(parsed.jobId);
          setIsPhotoSubmitted(true);
          setVerifyingSelfie(true);
        } else {
          sessionStorage.removeItem(SELFIE_JOB_ID_SIGNUP);
        }
      } catch {
        sessionStorage.removeItem(SELFIE_JOB_ID_SIGNUP);
      }
    }

    return () => {
      sessionStorage.removeItem(SELFIE_JOB_ID_SIGNUP);
    };
  }, [phoneNumber]);

  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const [secondErrorMessage, setSecondErrorMessage] = useState<
    string | undefined
  >(undefined);
  const [jobId, setJobId] = useState<string | undefined>("");
  const [selfieStatus, setSelfieStatus] = useState<string | undefined>(
    undefined
  );
  const [backModal, setBackModal] = useState(false);
  const [showSmileIdCamera, setShowSmileIdCamera] = useState(false);
  const [capturedImages, setCapturedImages] = useState<ImageSelfieRequestDTO[]>(
    []
  );
  const [isPhotoSubmitted, setIsPhotoSubmitted] = useState<boolean>(false);
  const [verifyingSelfie, setVerifyingSelfie] = useState<boolean>(false);
  const [isContinueDisabled, setIsContinueDisabled] = useState(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isCreatingAccount, setIsCreatingAccount] = useState<boolean>(false);
  const [isAccountCreationFailed, setIsAccountCreationFailed] =
    useState<boolean>(false);
  const [accountCreationError, setAccountCreationError] = useState<string>("");

  // Countdown timer state (seconds remaining out of 5 minutes)
  const [countdownSeconds, setCountdownSeconds] = useState<number>(
    SELFIE_VERIFICATION_COUNTDOWN_SECONDS
  );
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const smileSubmittedRef = useRef<boolean>(false);
  const accountCreationStartedRef = useRef<boolean>(false);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );
  const smileIdRef = useRef<HTMLElement | null>(null);

  const { isComponentLoaded } = useSmileIdWebComponent();

  useEffect(() => {
    if (!phoneNumber || !countryCode) {
      const navEntry = performance.getEntriesByType("navigation")[0] as
        | PerformanceNavigationTiming
        | undefined;
      if (navEntry?.type !== "reload") {
        navigateTo(SIGN_UP);
      }
    }
  }, [phoneNumber, countryCode, navigateTo]);

  // Bfcache restoration: browser unfreezes frozen JS state without re-running useEffects.
  // Reset to initial state so the user doesn't land on a stale spinner or dead polling loop.
  useEffect(() => {
    const handlePageShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return;

      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      sessionStorage.removeItem(SELFIE_JOB_ID_SIGNUP);
      setJobId("");
      setCapturedImages([]);
      smileSubmittedRef.current = false;
      setIsPhotoSubmitted(false);
      setVerifyingSelfie(false);
      setSelfieStatus(undefined);
      setErrorMessage(undefined);
      setSecondErrorMessage(undefined);
    };

    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  // Start / stop countdown timer when verification is in progress
  useEffect(() => {
    if (verifyingSelfie && isPhotoSubmitted) {
      setCountdownSeconds(SELFIE_VERIFICATION_COUNTDOWN_SECONDS);
      setElapsedSeconds(0);

      countdownRef.current = setInterval(() => {
        setCountdownSeconds((prev) => {
          if (prev <= 1) {
            clearInterval(countdownRef.current!);
            return 0;
          }
          return prev - 1;
        });
        setElapsedSeconds((prev) => prev + 1);
      }, 1000);
    } else {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    }

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, [verifyingSelfie, isPhotoSubmitted]);

  // When the countdown reaches zero while still verifying, stop polling and prompt retry
  useEffect(() => {
    if (countdownSeconds !== 0 || !verifyingSelfie) return;

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    sessionStorage.removeItem(SELFIE_JOB_ID_SIGNUP);
    setVerifyingSelfie(false);
    setErrorMessage(
      "Verification timed out. Please try again with a new photo."
    );
    setSecondErrorMessage("The 5-minute verification window has expired.");
  }, [countdownSeconds, verifyingSelfie]);

  const formatCountdown = (seconds: number): string => {
    const m = Math.floor(seconds / 60).toString();
    const s = (seconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const cleanBase64 = (base64String: string): string => {
    if (base64String.startsWith("data:image/")) {
      return base64String.split(",")[1];
    }
    return base64String;
  };

  const convertSmileIdToApiFormat = useCallback(
    (smileIdImages: { image: string }[]): ImageSelfieRequestDTO[] => {
      return smileIdImages.map((img, index) => ({
        imageContent: cleanBase64(img.image),
        imageType:
          index === smileIdImages.length - 1
            ? ImageSelfieRequestDTO.imageType.SELFIE_BASE64
            : ImageSelfieRequestDTO.imageType.LIVENESS_BASE64,
        mimeExt: "jpeg",
      }));
    },
    []
  );

  // Inject CSS into SmileID's nested shadow DOM.
  // selfie-capture-instructions is a light DOM child of selfie-capture-screens.
  // Its own shadow root holds the actual instruction screen content.
  useEffect(() => {
    if (!showSmileIdCamera) return;

    const el = smileIdRef.current;
    if (!el) return;

    const outerShadow = (el as HTMLElement & { shadowRoot: ShadowRoot | null })
      .shadowRoot;

    const STYLE_CONTENT = `
      /* Reduce main screen padding */
      #selfie-capture-instruction-screen {
        padding-block: 0.75rem !important;
      }

      /* Shrink the large header illustration */
      header svg { height: 40px !important; width: auto !important; }
      header { padding-block: 4px !important; }

      /* Compact instruction list */
      .instructions-wrapper {
        gap: 0.625rem !important;
        margin-block-start: 0.5rem !important;
        margin-block-end: 0.5rem !important;
      }

      /* Reduce icon margin inside each instruction row */
      .instructions svg { margin-inline-end: 0.75rem !important; }

      /* Reduce spacing between all flow children */
      .flow > * + * { margin-top: 0.5rem !important; }

      /* Hide SmileID's own nav — we supply our own close button */
      smileid-nav, smileid-nav *,
      header > button, header nav, header nav button,
      .back-button, .close-button,
      button[aria-label="back"], button[aria-label="close"],
      [data-cy="back-button"], [data-cy="close-button"] {
        display: none !important;
      }
    `;

    // Hide nav buttons on the camera capture screen (outer selfie-capture-screens shadow)
    // and recolor the success oval from green → dark red to match brand.
    const OUTER_NAV_STYLE = `
      smileid-nav, smileid-nav *,
      header > button, header nav, header nav button,
      .back-button, .close-button,
      button[aria-label="back"], button[aria-label="close"],
      [data-cy="back-button"], [data-cy="close-button"] {
        display: none !important;
      }

    `;

    if (outerShadow) {
      try {
        const outerSheet = new CSSStyleSheet();
        outerSheet.replaceSync(OUTER_NAV_STYLE);
        outerShadow.adoptedStyleSheets = [
          ...outerShadow.adoptedStyleSheets,
          outerSheet,
        ];
      } catch {
        const OUTER_STYLE_ID = "zenz-smileid-outer-styles";
        if (!outerShadow.getElementById(OUTER_STYLE_ID)) {
          const outerStyle = document.createElement("style");
          outerStyle.id = OUTER_STYLE_ID;
          outerStyle.textContent = OUTER_NAV_STYLE;
          outerShadow.appendChild(outerStyle);
        }
      }
    }

    let injectedSheet: CSSStyleSheet | null = null;
    let lastInnerShadow: ShadowRoot | null = null;

    const getInnerShadow = (): ShadowRoot | null => {
      // selfie-capture-instructions is a light DOM child of selfie-capture-screens,
      // not inside its shadow root — query the host element directly.
      const instructionEl =
        (el as HTMLElement).querySelector("selfie-capture-instructions") ??
        outerShadow?.querySelector("selfie-capture-instructions");
      if (!instructionEl) return null;
      return (
        (instructionEl as HTMLElement & { shadowRoot: ShadowRoot | null })
          .shadowRoot ?? null
      );
    };

    const overrideInlineFlowSpace = (shadow: ShadowRoot) => {
      shadow.querySelectorAll<HTMLElement>("section.flow").forEach((flowEl) => {
        if (flowEl.style.getPropertyValue("--flow-space") !== "0.75rem") {
          flowEl.style.setProperty("--flow-space", "0.75rem");
        }
      });
    };

    const injectIntoInnerShadow = () => {
      const innerShadow = getInnerShadow();
      if (!innerShadow) return;

      // New inner shadow root detected — (re-)inject the stylesheet.
      if (innerShadow !== lastInnerShadow) {
        lastInnerShadow = innerShadow;
        try {
          injectedSheet = new CSSStyleSheet();
          injectedSheet.replaceSync(STYLE_CONTENT);
          innerShadow.adoptedStyleSheets = [
            ...innerShadow.adoptedStyleSheets,
            injectedSheet,
          ];
        } catch {
          // Safari <16.4 fallback
          const STYLE_ID = "zenz-smileid-styles";
          if (!innerShadow.getElementById(STYLE_ID)) {
            const style = document.createElement("style");
            style.id = STYLE_ID;
            style.textContent = STYLE_CONTENT;
            innerShadow.appendChild(style);
          }
        }
      }

      overrideInlineFlowSpace(innerShadow);
    };

    // The camera oval lives inside selfie-capture-wrapper's own shadow root.
    // SVG structure: svg > path (base oval, stroke="#DA291C") + path (progress arc, stroke="#22c55e")
    // We only override the second path (progress arc) using svg > path + path,
    // leaving the base oval at its theme color.
    const WRAPPER_OVAL_STYLE = `svg > path + path { stroke: #7B0000 !important; }`;
    let lastWrapperShadow: ShadowRoot | null = null;

    const injectIntoWrapperShadow = () => {
      const wrapperEl =
        (el as HTMLElement).querySelector("selfie-capture-wrapper") ??
        outerShadow?.querySelector("selfie-capture-wrapper");
      if (!wrapperEl) return;

      const wrapperShadow = (
        wrapperEl as HTMLElement & { shadowRoot: ShadowRoot | null }
      ).shadowRoot;
      if (!wrapperShadow || wrapperShadow === lastWrapperShadow) return;

      lastWrapperShadow = wrapperShadow;
      try {
        const sheet = new CSSStyleSheet();
        sheet.replaceSync(WRAPPER_OVAL_STYLE);
        wrapperShadow.adoptedStyleSheets = [
          ...wrapperShadow.adoptedStyleSheets,
          sheet,
        ];
      } catch {
        const WRAPPER_STYLE_ID = "zenz-smileid-wrapper-styles";
        if (!wrapperShadow.getElementById(WRAPPER_STYLE_ID)) {
          const style = document.createElement("style");
          style.id = WRAPPER_STYLE_ID;
          style.textContent = WRAPPER_OVAL_STYLE;
          wrapperShadow.appendChild(style);
        }
      }
    };

    const injectAll = () => {
      injectIntoInnerShadow();
      injectIntoWrapperShadow();
    };

    injectAll();

    // Watch the host element's light DOM (where selfie-capture-instructions lives)
    // and the outer shadow root, for when SmileID adds/replaces child elements.
    const observer = new MutationObserver(injectAll);
    observer.observe(el as HTMLElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style"],
    });

    return () => {
      observer.disconnect();
      if (injectedSheet && lastInnerShadow) {
        lastInnerShadow.adoptedStyleSheets =
          lastInnerShadow.adoptedStyleSheets.filter((s) => s !== injectedSheet);
      }
    };
  }, [showSmileIdCamera]);

  useEffect(() => {
    if (isComponentLoaded && showSmileIdCamera) {
      const smileIdElement = smileIdRef.current;

      if (smileIdElement) {
        const handleSelfieCapture = (event: CustomEvent) => {
          if (event.detail && event.detail.images) {
            const converted = convertSmileIdToApiFormat(event.detail.images);
            setCapturedImages(converted);
            setShowSmileIdCamera(false);
          }
        };

        const handleSelfieCancel = () => setShowSmileIdCamera(false);
        const handleSelfieBack = () => setShowSmileIdCamera(false);

        smileIdElement.addEventListener(
          "selfie-capture-screens.publish",
          handleSelfieCapture as EventListener
        );
        smileIdElement.addEventListener(
          "selfie-capture-screens.cancelled",
          handleSelfieCancel as EventListener
        );
        smileIdElement.addEventListener(
          "selfie-capture-screens.back",
          handleSelfieBack as EventListener
        );

        return () => {
          smileIdElement.removeEventListener(
            "selfie-capture-screens.publish",
            handleSelfieCapture as EventListener
          );
          smileIdElement.removeEventListener(
            "selfie-capture-screens.cancelled",
            handleSelfieCancel as EventListener
          );
          smileIdElement.removeEventListener(
            "selfie-capture-screens.back",
            handleSelfieBack as EventListener
          );
        };
      }
    }
  }, [isComponentLoaded, showSmileIdCamera, convertSmileIdToApiFormat]);

  const handleOpenCamera = useCallback(() => {
    if (isComponentLoaded) {
      setShowSmileIdCamera(true);
    }
  }, [isComponentLoaded]);

  const handleModalState = () => setBackModal(!backModal);

  const handleDiscardSignup = () => {
    setJobId("");
    setSelfieStatus(undefined);
    sessionStorage.removeItem(SELFIE_JOB_ID_SIGNUP);
    navigateTo(SIGN_UP);
  };

  const handleDiscardAll = () => {
    sessionStorage.removeItem(SELFIE_JOB_ID_SIGNUP);
    setJobId("");
    setCapturedImages([]);
    smileSubmittedRef.current = false;
    accountCreationStartedRef.current = false;
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsPhotoSubmitted(false);
    setVerifyingSelfie(false);
    setSelfieStatus(undefined);
    setIsAccountCreationFailed(false);
    setAccountCreationError("");
    navigateTo(SIGN_UP);
  };

  const handleTryAgain = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setVerifyingSelfie(false);
    setJobId("");
    setCapturedImages([]);
    smileSubmittedRef.current = false;
    accountCreationStartedRef.current = false;
    sessionStorage.removeItem(SELFIE_JOB_ID_SIGNUP);
    setIsPhotoSubmitted(false);
    setSelfieStatus(undefined);
    setErrorMessage(undefined);
    setSecondErrorMessage(undefined);
  };

  // Submit captured images to onboarding API, receive jobId, then start polling
  useEffect(() => {
    if (capturedImages.length === 0 || smileSubmittedRef.current) return;
    smileSubmittedRef.current = true;
    setIsContinueDisabled(true);
    setIsUploading(true);

    OnboardingApiService.submitOnboardingSelfie({
      images: capturedImages,
      phoneNumber: phoneNumber || "",
      countryCode: countryCodeToEnum(countryCode),
    })
      .then((response) => {
        setIsUploading(false);
        setIsContinueDisabled(false);
        const newJobId = response.data?.jobId;
        if (newJobId) {
          setJobId(newJobId);
          sessionStorage.setItem(
            SELFIE_JOB_ID_SIGNUP,
            JSON.stringify({ jobId: newJobId, phoneNumber })
          );
        }
        setIsPhotoSubmitted(true);
        setVerifyingSelfie(true);
        setErrorMessage(undefined);
        setSecondErrorMessage(undefined);
      })
      .catch(() => {
        setIsUploading(false);
        setIsContinueDisabled(false);
        smileSubmittedRef.current = false;
        setErrorMessage("Failed to submit your selfie");
        setSecondErrorMessage(
          "Please try again or contact support if the issue persists"
        );
      });
  }, [capturedImages, phoneNumber, countryCode]);

  // Poll onboarding selfie status using jobId
  useEffect(() => {
    if (!verifyingSelfie || !jobId) return;

    const poll = async () => {
      try {
        const response = await OnboardingApiService.getOnboardingSelfieStatus(
          jobId
        );
        if (!response.data) return;

        const { status, message } = response.data;

        if (
          status === SelfieStatusResponseDTO.status.VERIFIED_SUCCESSFULLY ||
          message === SelfieStatusResponseDTO.message.VERIFIED_SUCCESSFULLY
        ) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setSelfieStatus("VERIFIED_SUCCESSFULLY");
          setTimeout(() => {
            setVerifyingSelfie(false);
            nextStep();
          }, 500);
        } else if (
          message &&
          [
            SelfieStatusResponseDTO.message.NO_FACE_FOUND,
            SelfieStatusResponseDTO.message.LOW_IMAGE_QUALITY,
            SelfieStatusResponseDTO.message.GENERAL_ERROR_IN_VERIFICATION,
          ].includes(message)
        ) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setVerifyingSelfie(false);
          setSelfieStatus("ERROR_IN_VERIFICATION");
          setErrorMessage("We could not verify your BVN");
          setSecondErrorMessage("Please try again");
        }
      } catch {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        setVerifyingSelfie(false);
        setSelfieStatus("ERROR_IN_VERIFICATION");
        setErrorMessage("There was a problem verifying your selfie");
        setSecondErrorMessage("Please try again with a clearer photo");
      }
    };

    poll();
    pollingIntervalRef.current = setInterval(poll, 5000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyingSelfie, jobId]);

  const renderInstructions = () => (
    <>
      <ul className="flex flex-col gap-y-8 text-base-lh-17 font-normal text-neutral-600">
        <li className="flex flex-row">
          <Camera className="mt-1" />
          <p className="ml-3 w-full">
            We need access to your camera for face verification and to capture
            your photo
          </p>
        </li>
        <li className="flex flex-row">
          <Smile className="mt-1" />
          <p className="ml-3 w-full">
            Please, face the camera and keep a straight face
          </p>
        </li>
        <li className="flex flex-row">
          <Home className="mt-1" />
          <p className="ml-3 w-full">
            Ensure you are in a well lit environment
          </p>
        </li>
        <li className="flex flex-row">
          <Eye className="mt-1" />
          <p className="ml-3 w-full">
            Remove contacts, glasses and other facial accessories
          </p>
        </li>
      </ul>
      <div className="bg-neutral-info my-12 flex w-full items-start rounded-1.25l p-4">
        <p className="mt-0.5">
          <Info />
        </p>
        <p className="text-neutral-850 ml-3 text-sm font-normal">
          Your photograph is safe and will only be used for identification
          purposes.
        </p>
      </div>
    </>
  );

  const shouldShowErrorUI = () => {
    return (
      selfieStatus === "ERROR_IN_VERIFICATION" ||
      selfieStatus === "NOT_VERIFIED" ||
      !!errorMessage
    );
  };

  return (
    <>
      <ModalComponent
        body="Are you sure you want to leave?"
        secondLine="All information provided will be lost."
        title="Cancel Signup"
        classNames={`w-[98%] sm:w-2/3 lg:1/3`}
        showModal={backModal}
        onClickClose={handleModalState}
        doubleOptions={true}
        onLeftButtonClick={handleDiscardSignup}
        onRightButtonClick={handleModalState}
        leftButtonText="Cancel Signup"
        rightButtonText="Continue Signup"
      />

      <div className="absolute top-3 left-1/2 flex -translate-x-1/2 gap-2 lg:hidden">
        <WalletIcon height={40} width={40} className="my-auto" />
        <Logo />
      </div>

      {showSmileIdCamera && (
        <div className="fixed inset-0 z-50 overflow-y-auto overflow-x-hidden bg-black bg-opacity-75">
          <div className="flex min-h-full items-center justify-center py-4">
            <style>{`.smile-card::-webkit-scrollbar{display:none}`}</style>
            <div
              className="smile-card relative mx-auto max-h-[90vh] min-h-[600px] w-full max-w-lg overflow-y-auto rounded-lg bg-white px-4"
              style={
                {
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                } as React.CSSProperties
              }
            >
              <button
                onClick={() => setShowSmileIdCamera(false)}
                className="absolute top-4 right-4 z-20 rounded-full bg-white p-2 shadow-lg"
              >
                <CloseCircle width={24} height={24} />
              </button>
              <selfie-capture-screens
                ref={smileIdRef}
                theme-color={"#DA291C"}
                hide-back-to-host
                style={
                  {
                    "--color-success": "#8B0000",
                    "--smile-color-success": "#8B0000",
                    "--color-active": "#8B0000",
                    "--capture-success-color": "#8B0000",
                    "--color-valid": "#8B0000",
                    "--smileid-color-success": "#8B0000",
                  } as React.CSSProperties
                }
              />
            </div>
          </div>
        </div>
      )}

      <h1 className="text-neutral-850 mb-10 text-center text-4.5xl font-medium">
        Photo Verification
      </h1>
      {!isPhotoSubmitted && !verifyingSelfie && (
        <p className="mb-8 text-center text-base text-neutral-600">
          Complete your account setup with photo verification
        </p>
      )}

      {shouldShowErrorUI() ? (
        <div className="mt-8">
          <div className="mb-4 flex items-center">
            <CloseCircle stroke="#DA291C" width={32} height={32} />
            <h4 className="ml-2 text-xl">Photo Verification Issue</h4>
          </div>
          <p className="mb-2 text-sm text-neutral-600">
            {errorMessage || "We could not verify your BVN"}
          </p>
          <p className="mb-8 text-sm text-neutral-400">
            {secondErrorMessage || "Please try again"}
          </p>
          <div className="flex gap-4">
            <button
              className="secondary-button"
              onClick={handleModalState}
              type="button"
            >
              Cancel Signup
            </button>
            <button
              className="primary-button"
              onClick={handleTryAgain}
              type="button"
            >
              Try Again
            </button>
          </div>
        </div>
      ) : !isPhotoSubmitted ? (
        <>
          {renderInstructions()}
          <div className="stepper-buttons-container mx-auto w-full">
            <button
              id="continue-to-photo-verification"
              className="primary-button w-full"
              onClick={handleOpenCamera}
              disabled={isContinueDisabled || !isComponentLoaded || isUploading}
            >
              <div className="flex w-full items-center justify-center gap-2">
                <span>
                  {!isComponentLoaded
                    ? "Loading..."
                    : "Start Photo Verification"}
                </span>
                {isUploading && <SpinnerComponent type={"primary"} size={20} />}
              </div>
            </button>
          </div>
        </>
      ) : isPhotoSubmitted ? (
        <div className="mx-auto flex flex-col items-center justify-center text-center">
          {verifyingSelfie && (
            <div className="mx-auto flex w-full flex-col items-center text-center">
              <SpinnerComponent size={64} type={"primary"} showIcon={false} />
              <p className="mt-6 text-base font-normal text-neutral-600">
                Your photograph is being verified.
              </p>
              <p className="text-sm font-normal text-neutral-400">
                This should not take too long.
              </p>
              <div className="mt-4 flex items-center gap-2 rounded-[10px] bg-[#5656CA14] px-4 py-3">
                <Clock width={16} height={16} stroke="#1A1C1F" />
                <span className="font-medium] text-sm text-[#1A1C1F]">
                  {formatCountdown(countdownSeconds)} Minute
                  {countdownSeconds !== 1 ? "s" : ""}
                </span>
              </div>
              {elapsedSeconds >= SELFIE_VERIFICATION_SLOW_THRESHOLD_SECONDS && (
                <div className="mt-6 flex w-full items-start rounded-[10px] border border-amber-200 bg-[#F0B02A14] p-4">
                  <Info
                    className="mt-0.5 shrink-0 text-[#B8810E]"
                    stroke="#B8810E"
                  />
                  <p className="ml-3 text-sm font-normal text-[#B8810E]">
                    Verification is taking a little longer than usual. Please
                    hold on while we complete the process
                  </p>
                </div>
              )}
            </div>
          )}

          {!verifyingSelfie &&
            (selfieStatus === "APPROVED" ||
              selfieStatus === "VERIFIED" ||
              selfieStatus === "VERIFIED_SUCCESSFULLY") && (
              <div className="mt-8">
                <div className="mb-4 flex items-center">
                  <GreenTick width={32} height={32} />
                  <h4 className="ml-2 text-xl">Photo Successfully Verified</h4>
                </div>
                <p className="mb-8 text-sm text-neutral-600">
                  Your photo has been verified. Creating your account now...
                </p>
              </div>
            )}

          {!verifyingSelfie && selfieStatus === "PENDING" && (
            <div className="mt-8">
              <div className="mb-4 flex items-center">
                <Clock width={32} height={32} />
                <h4 className="ml-2 text-xl">Waiting For Approval</h4>
              </div>
              <p className="mb-8 text-sm text-neutral-600">
                Your photo is being reviewed. This usually takes a few moments.
              </p>
              <div className="flex gap-4">
                <button
                  className="secondary-button"
                  onClick={handleModalState}
                  type="button"
                >
                  Cancel Signup
                </button>
              </div>
            </div>
          )}
        </div>
      ) : null}
    </>
  );
});

export default SignUpPhotoVerificationPage;
